import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faTimes, faPenToSquare, faArrowUpRightFromSquare, faPaperclip,
    faRepeat, faUserCheck, faCircleCheck, faClock, faTriangleExclamation,
    faHourglassHalf, faCamera, faCheck, faXmark
} from '@fortawesome/free-solid-svg-icons';
import api from '../utils/api';
import RichText from './RichText';
import { decideTask } from '../utils/taskApproval';
import Avatar from './Avatar';
import { shortDate, timeAgo } from '../utils/taskHelpers';
import '../styles/taskDrawer.css';

/**
 * The task behind a number, without leaving the page you were reading.
 *
 * Opened from a row on the Performance drill-in and on the employee profile's
 * Tasks tab. Those rows used to navigate straight to the full task page, which
 * lost the reviewer's place — their filters, their scroll position, and the
 * run of tasks they were working through. A drawer keeps all of that and puts
 * the whole record one click away instead.
 *
 * Read-only on purpose. Editing a task is a real decision with real
 * consequences for the person it is assigned to, so it happens on the edit
 * page where every field and its validation live — the button at the foot is
 * the way there, not a shortcut past it. `onEdit` is optional: leave it out
 * when the viewer may not edit this task and the button is not shown.
 *
 * `outcome` is passed in rather than recomputed. The list already knows how
 * each task scored (the server labelled it), and deriving it a second time
 * here is exactly how a drawer ends up disagreeing with the row that opened it.
 */

const OUTCOME = {
    onTime: { label: 'Completed on time', cls: 'completed', icon: faCircleCheck },
    late: { label: 'Completed late', cls: 'onhold', icon: faClock },
    missed: { label: 'Missed the deadline', cls: 'blocked', icon: faTriangleExclamation },
    open: { label: 'Still in hand', cls: 'today', icon: faHourglassHalf }
};

const dateTime = (value) => {
    if (!value) return null;
    const d = new Date(value);
    if (isNaN(d)) return null;
    return `${shortDate(d)}, ${d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
};

const minutesLabel = (mins) => {
    if (!mins) return null;
    const h = Math.floor(mins / 60);
    const m = mins % 60;
    return [h ? `${h}h` : null, m ? `${m}m` : null].filter(Boolean).join(' ') || null;
};

/*
 * `canDecide` (optional): the opener knows this viewer may approve the task, as
 * the chat does. While a self-assigned task is still waiting, the footer then
 * offers Approve and Reject, and onDecided hears the outcome.
 */
const TaskDetailDrawer = ({ taskId, outcome, onClose, onEdit, onOpenFull, canDecide = false, onDecided, renderExtra }) => {
    const [task, setTask] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [deciding, setDeciding] = useState(false);

    const decide = async (decision) => {
        setDeciding(true);
        const done = await decideTask(task._id, decision, task.assignees?.[0]?.name?.split(' ')[0]);
        setDeciding(false);
        if (!done) return;
        setTask((t) => ({ ...t, approvalStatus: done.decision, approvalNote: done.note }));
        onDecided?.(done.decision);
    };
    const awaitingMe = canDecide && task?.isSelfAssigned && task.approvalStatus === 'Pending';

    // Escape closes it, the way every other panel on the web does.
    useEffect(() => {
        const onKey = (e) => { if (e.key === 'Escape') onClose(); };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [onClose]);

    // The page behind must not scroll while the drawer is over it — otherwise
    // a scroll gesture aimed at the drawer moves the list underneath and the
    // reviewer loses the row they came from.
    useEffect(() => {
        const previous = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        return () => { document.body.style.overflow = previous; };
    }, []);

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        setError(null);

        api.get(`/tasks/${taskId}`)
            .then(res => { if (!cancelled) setTask(res.data); })
            .catch(err => {
                if (cancelled) return;
                setError(err.response?.data?.message || 'Could not load this task.');
            })
            .finally(() => { if (!cancelled) setLoading(false); });

        // A fast reviewer can open a second row before the first has landed.
        // Without this, the earlier response overwrites the later one.
        return () => { cancelled = true; };
    }, [taskId]);

    const verdict = outcome && OUTCOME[outcome];

    return (
        <>
            <div className="tdd-backdrop" onClick={onClose} />
            <aside className="tdd-panel" role="dialog" aria-modal="true" aria-label="Task details">
                <div className="tdd-head">
                    <div className="tdd-head-text">
                        <h3>{loading ? 'Loading task...' : (task?.title || 'Task')}</h3>
                        {task && (
                            <div className="tdd-sub">
                                {task.projectId ? task.projectId.name : 'Office work'}
                                {task.recurringTaskId && (
                                    <> &middot; <FontAwesomeIcon icon={faRepeat} /> Recurring</>
                                )}
                            </div>
                        )}
                    </div>
                    <button className="tdd-close" onClick={onClose} aria-label="Close">
                        <FontAwesomeIcon icon={faTimes} />
                    </button>
                </div>

                <div className="tdd-body">
                    {loading && <p className="tdd-muted">Loading...</p>}
                    {error && <p className="tdd-error">{error}</p>}

                    {task && !loading && (
                        <>
                            {verdict && (
                                <div className={`tdd-verdict ${verdict.cls}`}>
                                    <FontAwesomeIcon icon={verdict.icon} />
                                    <span>{verdict.label}</span>
                                </div>
                            )}

                            <div className="tdd-badges">
                                <span className={`task-status-badge ${task.status.toLowerCase().replace(/\s/g, '')}`}>
                                    {task.status}
                                </span>
                                <span className={`priority-badge ${task.priority?.toLowerCase()}`}>
                                    {task.priority}
                                </span>
                                {task.isSelfAssigned && (
                                    <span className="task-status-badge awaiting">
                                        Self-assigned{task.approvalStatus !== 'Approved' ? ` · ${task.approvalStatus}` : ''}
                                    </span>
                                )}
                                {task.isPrivate && (
                                    <span className="task-status-badge awaiting">Private</span>
                                )}
                            </div>

                            {task.description && (
                                <Section title="Brief">
                                    <RichText text={task.description} className="tdd-desc" />
                                </Section>
                            )}

                            <Section title="Timing">
                                <Row label="Due">
                                    {shortDate(task.occurrenceDate || task.dueDate)}
                                    {task.dueTime && <span className="tdd-dim"> at {task.dueTime}</span>}
                                </Row>
                                <Row label="Deadline passes">{dateTime(task.overdueAt) || '—'}</Row>
                                {task.startDate && <Row label="Start">{shortDate(task.startDate)}</Row>}
                                {minutesLabel(task.timeAllottedMinutes) && (
                                    <Row label="Time allotted">{minutesLabel(task.timeAllottedMinutes)}</Row>
                                )}
                                <Row label="Finished">
                                    {dateTime(task.firstCompletedAt || task.completedAt) || <span className="tdd-dim">Not yet</span>}
                                </Row>
                                <Row label="Created">
                                    {shortDate(task.createdAt)}
                                    <span className="tdd-dim"> · {timeAgo(task.createdAt)}</span>
                                </Row>
                            </Section>

                            <Section title="People">
                                <Row label="Assigned by">
                                    {task.assignedBy ? (
                                        <span className="tdd-person">
                                            <Avatar
                                                name={task.assignedBy.name}
                                                profilePic={task.assignedBy.profilePic}
                                                className="tdd-avatar"
                                            />
                                            {task.assignedBy.name}
                                        </span>
                                    ) : '—'}
                                </Row>
                                <Row label={`Assignee${task.assignees?.length > 1 ? `s (${task.assignees.length})` : ''}`}>
                                    <span className="tdd-people">
                                        {(task.assignees || []).map(a => (
                                            <span className="tdd-person" key={a._id}>
                                                <Avatar name={a.name} profilePic={a.profilePic} className="tdd-avatar" />
                                                {a.name}
                                            </span>
                                        ))}
                                    </span>
                                </Row>
                                {task.externalAssignees?.length > 0 && (
                                    <Row label={`Guest${task.externalAssignees.length > 1 ? `s (${task.externalAssignees.length})` : ''}`}>
                                        <span className="tdd-people">
                                            {task.externalAssignees.map(v => (
                                                <span className="tdd-person" key={v._id}>
                                                    <Avatar name={v.name} profilePic={v.profilePic} className="tdd-avatar" />
                                                    {v.name}
                                                    <span className="ext-badge">Guest</span>
                                                </span>
                                            ))}
                                        </span>
                                    </Row>
                                )}
                                {task.approvedBy && (
                                    <Row label="Approved by">
                                        <span className="tdd-person">
                                            <FontAwesomeIcon icon={faUserCheck} className="tdd-dim" />
                                            {task.approvedBy.name}
                                        </span>
                                    </Row>
                                )}
                            </Section>

                            {(task.statusNote || task.statusUpdatedBy) && (
                                <Section title="Latest update">
                                    {task.statusNote && <p className="tdd-desc">{task.statusNote}</p>}
                                    {task.statusUpdatedBy && (
                                        <Row label="Moved by">{task.statusUpdatedBy.name}</Row>
                                    )}
                                </Section>
                            )}

                            {(task.attachments?.length > 0 || task.completionProof?.length > 0) && (
                                <Section title="Files">
                                    {task.attachments?.length > 0 && (
                                        <Row label="Attachments">
                                            <FontAwesomeIcon icon={faPaperclip} className="tdd-dim" />{' '}
                                            {task.attachments.length}
                                        </Row>
                                    )}
                                    {task.completionProof?.length > 0 && (
                                        <Row label="Proof of work">
                                            <FontAwesomeIcon icon={faCamera} className="tdd-dim" />{' '}
                                            {task.completionProof.length}
                                        </Row>
                                    )}
                                </Section>
                            )}

                            {/* The one thing a summary cannot show: how the task
                                actually moved. Not backfilled, so older tasks
                                have none — an empty history says nothing rather
                                than claiming nothing happened. */}
                            {task.statusHistory?.length > 0 && (
                                <Section title="History">
                                    <ol className="tdd-timeline">
                                        {[...task.statusHistory].reverse().map((h, i) => (
                                            <li key={i}>
                                                <span className="tdd-timeline-to">{h.to}</span>
                                                <span className="tdd-dim">
                                                    {h.from ? ` from ${h.from}` : ''} · {dateTime(h.at)}
                                                </span>
                                                {h.note && <span className="tdd-timeline-note">{h.note}</span>}
                                            </li>
                                        ))}
                                    </ol>
                                </Section>
                            )}

                            {/* Anything the opener adds below - the chat puts
                                the task's discussion here. */}
                            {renderExtra?.(task)}
                        </>
                    )}
                </div>

                <div className="tdd-foot">
                    {awaitingMe && (
                        <>
                            <button className="gts-btn tdd-reject" onClick={() => decide('Rejected')} disabled={deciding}>
                                <FontAwesomeIcon icon={faXmark} /> Reject
                            </button>
                            <button className="gts-btn tdd-approve" onClick={() => decide('Approved')} disabled={deciding}>
                                <FontAwesomeIcon icon={faCheck} /> Approve
                            </button>
                        </>
                    )}
                    <button
                        className="gts-btn secondary"
                        onClick={onOpenFull}
                        disabled={!task}
                        title="The full task page — discussion, proof of work and status"
                    >
                        <FontAwesomeIcon icon={faArrowUpRightFromSquare} /> Open full task
                    </button>
                    {onEdit && (
                        <button
                            className="gts-btn primary"
                            onClick={onEdit}
                            disabled={!task}
                        >
                            <FontAwesomeIcon icon={faPenToSquare} /> Edit task
                        </button>
                    )}
                </div>
            </aside>
        </>
    );
};

const Section = ({ title, children }) => (
    <div className="tdd-section">
        <div className="tdd-section-title">{title}</div>
        {children}
    </div>
);

const Row = ({ label, children }) => (
    <div className="tdd-row">
        <span className="tdd-row-label">{label}</span>
        <span className="tdd-row-value">{children}</span>
    </div>
);

export default TaskDetailDrawer;
